/**
 * cardiacPhases.js — фазы сердечного цикла и фракция выброса: чистая математика.
 *
 * ПОВОД, и он мой. Четыре выпуска подряд я писал в журнале: «4D и ФВ по-прежнему
 * без тестов — самое опасное для кардиолога место». Место опасное потому, что
 * ошибка здесь не роняет программу и не рисует кракозябры: она печатает число.
 * «ФВ 58%» выглядит одинаково убедительно и когда посчитано верно, и когда
 * систола перепутана с диастолой. Врач не может это проверить глазами.
 *
 * Разбор, ради которого модуль и появился, нашёл в 4D три вещи, каждая из
 * которых способна выдать неверную ФВ:
 *
 * 1. **В «процент фазы» складывались несопоставимые величины.** Порядок был
 *    такой: процент фазы → время задержки в МИЛЛИСЕКУНДАХ → триггерное время в
 *    МИЛЛИСЕКУНДАХ → ЧИСЛО КАДРОВ. Для серии с триггерными временами 0…800 мс
 *    получались «фазы» 0…800%, а подписи «Систола» и «Диастола» ставились по
 *    окнам 25–45% и 65–85% — то есть не ставились вовсе или ставились не туда.
 *    Число кадров в качестве процента фазы — это уже не приближение, а мусор.
 *
 * 2. **Систола и диастола выбирались по подписи, а не по объёму.** Между тем ФВ
 *    определена через ОБЪЁМЫ: КДО — наибольший, КСО — наименьший. Если подписи
 *    сдвинуты (см. пункт 1), формула считает разность не тех фаз, и знак может
 *    даже перевернуться.
 *
 * 3. **Объём «ЛЖ» считался как все воксели 150–500 HU во всём кадре.** Это не
 *    левый желудочек: в окно попадают аорта, правый желудочок, предсердия и
 *    любой контраст в кадре. Числа «КДО 380 мл» физиологически невозможны
 *    (норма 65–200 мл), а разность двух неверных объёмов не становится верной ФВ.
 *
 * ЧТО ЗДЕСЬ. Только математика и правила, которые можно проверить на известном
 * ответе, без браузера и без DICOM. Всё, что нельзя посчитать честно,
 * возвращает `надёжно: false` и причину — и вызывающий обязан сказать её врачу,
 * а не показать число.
 *
 * НОРМЫ, на которые опираются признаки правдоподобия (не диагноз, а рамки
 * здравого смысла): ФВ ЛЖ у взрослого 52–72% (мужчины) и 54–74% (женщины) по
 * рекомендациям ASE/EACVI 2015; КДО ЛЖ примерно 62–150 мл, КСО 21–61 мл.
 * Значения вне этих рамок не запрещены — они помечаются как требующие проверки.
 */

/** Как получен процент фазы. От этого зависит, можно ли верить подписям. */
export const МЕТОД = {
  ПРОЦЕНТ: 'процент фазы из тега',
  ПО_ВРЕМЕНИ: 'пересчёт из времени задержки',
  ПОРЯДОК: 'только порядок кадров',
};

const ЧИСЛО = (v) => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/**
 * Проценты фаз из метаданных серий — честно и с указанием метода.
 *
 * Принимается список серий вида
 *   { metadata: { nominalPercentageOfCardiacPhase?, triggerTime?, nominalInterval?, ... } }
 *
 * Правила:
 *   · если есть процент фазы (0…100) — берём его;
 *   · иначе, если есть триггерные времена — переводим в проценты по интервалу
 *     R-R: процент = 100 · TT / R-R. R-R берётся из nominalInterval, а если его
 *     нет — оценивается по самому позднему триггерному времени (тогда это
 *     сказано в примечании, потому что последняя фаза окажется ровно на 100%);
 *   · иначе процента НЕТ. Возвращается null, и подписи «систола/диастола» не
 *     ставятся: выдумывать проценты из номера кадра нельзя.
 *
 * @returns {{фазы: Array, метод: string, примечания: string[]}}
 */
export function фазыИзМетаданных(серии) {
  const примечания = [];
  if (!Array.isArray(серии) || !серии.length) {
    return { фазы: [], метод: МЕТОД.ПОРЯДОК, примечания: ['список серий пуст'] };
  }

  const собрано = серии.map((с, i) => {
    const м = с?.metadata || {};
    return {
      исходный: с,
      порядок: i,
      процентТег: ЧИСЛО(м.nominalPercentageOfCardiacPhase ?? м.phasePercent ?? м.nominalPercentage),
      время: ЧИСЛО(м.triggerTime ?? м.nominalCardiacTriggerDelayTime),
      интервал: ЧИСЛО(м.nominalInterval ?? м.rrInterval),
      // Число кадров в цикле НЕ используется как процент — это счётчик.
      кадровВЦикле: ЧИСЛО(м.cardiacNumberOfImages),
    };
  });

  if (собрано.some((с) => с.кадровВЦикле !== null) && собрано.every((с) => с.процентТег === null && с.время === null)) {
    примечания.push('в метаданных есть только число кадров в цикле (cardiacNumberOfImages) — это счётчик, а не процент фазы: проценты не определены');
  }

  // 1. Процент из тега.
  if (собрано.every((с) => с.процентТег !== null && с.процентТег >= 0 && с.процентТег <= 100)) {
    const фазы = собрано
      .map((с) => ({ ...с.исходный, порядок: с.порядок, процент: с.процентТег }))
      .sort((a, b) => a.процент - b.процент)
      .map((ф, i) => ({ ...ф, индекс: i, подпись: подписьФазы(ф.процент, МЕТОД.ПРОЦЕНТ) }));
    return { фазы, метод: МЕТОД.ПРОЦЕНТ, примечания };
  }

  // 2. Пересчёт из времени задержки.
  const времена = собрано.map((с) => с.время).filter((т) => т !== null && т >= 0);
  if (времена.length === собрано.length && времена.length >= 2) {
    let rr = собрано.find((с) => с.интервал !== null && с.интервал > 0)?.интервал ?? null;
    if (!rr) {
      const макс = Math.max(...времена);
      const шаг = времена.length > 1 ? макс / (времена.length - 1) : макс;
      rr = макс + шаг;                       // последняя фаза не совпадает с началом следующего цикла
      примечания.push(`интервал R-R в метаданных не указан — оценён по временам задержки как ${Math.round(rr)} мс; проценты фаз относительные`);
    }
    const фазы = собрано
      .map((с) => ({ ...с.исходный, порядок: с.порядок, процент: +(100 * с.время / rr).toFixed(1), времяМс: с.время }))
      .sort((a, b) => a.процент - b.процент)
      .map((ф, i) => ({ ...ф, индекс: i, подпись: подписьФазы(ф.процент, МЕТОД.ПО_ВРЕМЕНИ) }));
    return { фазы, метод: МЕТОД.ПО_ВРЕМЕНИ, примечания, интервалМс: Math.round(rr) };
  }

  // 3. Процента нет — только порядок.
  примечания.push('ни процента фазы, ни времени задержки в метаданных нет: фазы упорядочены как пришли, подписи «систола/диастола» не ставятся');
  const фазы = собрано.map((с, i) => ({
    ...с.исходный, порядок: с.порядок, процент: null, индекс: i,
    подпись: `Фаза ${i + 1} из ${собрано.length}`,
  }));
  return { фазы, метод: МЕТОД.ПОРЯДОК, примечания };
}

/**
 * Подпись фазы по проценту R-R.
 *
 * Соглашение ретроспективной ЭКГ-синхронизации: 0% — вершина R (конец
 * диастолы), конец систолы около 30–40%, диастаза (покой, лучшее окно для
 * коронаров) около 70–80%. Подпись ставится только там, где процент известен.
 */
export function подписьФазы(процент, метод = МЕТОД.ПРОЦЕНТ) {
  const p = ЧИСЛО(процент);
  if (p === null || метод === МЕТОД.ПОРЯДОК) return 'Фаза';
  const округ = Math.round(p);
  if (p <= 10 || p >= 95) return `Конец диастолы (${округ}%)`;
  if (p >= 25 && p <= 45) return `Конец систолы (${округ}%)`;
  if (p >= 65 && p <= 85) return `Диастаза (${округ}%)`;
  return `Фаза ${округ}%`;
}

/**
 * Выбор фаз для ФВ ПО ИЗМЕРЕННЫМ ОБЪЁМАМ, а не по подписи.
 *
 * КДО — наибольший объём, КСО — наименьший. Если подписи фаз есть и они
 * расходятся с измерением, это возвращается предупреждением: расхождение —
 * повод посмотреть, а не повод молча доверять любому из двух.
 *
 * @param {number[]} объёмы объём по каждой фазе (мл), NaN/null допустимы
 * @param {Array<{подпись?: string, процент?: number}>} фазы необязательно
 */
export function выбратьФазыДляФВ(объёмы, фазы = []) {
  const пригодные = (объёмы || [])
    .map((v, i) => ({ i, v: ЧИСЛО(v) }))
    .filter((о) => о.v !== null && о.v > 0);

  if (пригодные.length < 2) {
    return { надёжно: false, причина: `для ФВ нужны объёмы минимум двух фаз, пригодных ${пригодные.length}`, предупреждения: [] };
  }

  let ксо = пригодные[0], кдо = пригодные[0];
  for (const о of пригодные) {
    if (о.v < ксо.v) ксо = о;
    if (о.v > кдо.v) кдо = о;
  }
  if (ксо.i === кдо.i || !(кдо.v > ксо.v)) {
    return { надёжно: false, причина: 'все фазы дали одинаковый объём — систолу от диастолы не отличить', предупреждения: [] };
  }

  const предупреждения = [];
  const подписьКДО = фазы[кдо.i]?.подпись || '';
  const подписьКСО = фазы[ксо.i]?.подпись || '';
  if (/систол/i.test(подписьКДО)) {
    предупреждения.push(`наибольший объём пришёлся на фазу «${подписьКДО}» — измерение и подпись расходятся, проверьте синхронизацию с ЭКГ`);
  }
  if (/диастол|диастаза/i.test(подписьКСО)) {
    предупреждения.push(`наименьший объём пришёлся на фазу «${подписьКСО}» — измерение и подпись расходятся`);
  }

  return {
    надёжно: true, причина: null,
    индексКДО: кдо.i, индексКСО: ксо.i,
    кдо: кдо.v, ксо: ксо.v,
    предупреждения,
  };
}

/**
 * Фракция выброса: ФВ = (КДО − КСО) / КДО × 100.
 *
 * Проверяется всё, что может сделать число бессмысленным, и каждый отказ имеет
 * причину. Правдоподобие — отдельно от правильности: ФВ 12% посчитана верно, но
 * это либо тяжёлая кардиомиопатия, либо ошибка сегментации, и программа обязана
 * сказать об этом, а не показать «12%» как обычное число.
 */
export function фракцияВыброса({ кдо, ксо } = {}) {
  const КДО = ЧИСЛО(кдо), КСО = ЧИСЛО(ксо);
  const пусто = { надёжно: false, фв: null, кдо: КДО, ксо: КСО, уо: null, предупреждения: [] };

  if (КДО === null || КСО === null) return { ...пусто, причина: 'КДО и КСО должны быть числами' };
  if (КДО <= 0) return { ...пусто, причина: 'КДО должен быть больше нуля' };
  if (КСО < 0) return { ...пусто, причина: 'КСО не может быть отрицательным' };
  if (КСО >= КДО) {
    return { ...пусто, причина: `КСО (${КСО} мл) не меньше КДО (${КДО} мл): фазы перепутаны либо объёмы посчитаны неверно` };
  }

  const уо = КДО - КСО;                       // ударный объём
  const фв = +((уо / КДО) * 100).toFixed(1);

  const предупреждения = [];
  if (фв < 52) предупреждения.push(`ФВ ${фв}% ниже нормы (52–72% по ASE/EACVI) — если это не ожидаемо клинически, проверьте сегментацию`);
  if (фв > 80) предупреждения.push(`ФВ ${фв}% выше физиологически обычного — почти всегда признак того, что КСО занижен (в маску попало не всё)`);
  if (КДО > 250) предупреждения.push(`КДО ${КДО} мл выше обычного диапазона (62–150 мл): похоже, в объём попали не только ЛЖ`);
  if (КДО < 40) предупреждения.push(`КДО ${КДО} мл ниже обычного диапазона: маска, вероятно, охватила не весь желудочек`);

  return { надёжно: true, причина: null, фв, кдо: КДО, ксо: КСО, уо, предупреждения };
}

/**
 * Объём по МАСКЕ — единственный способ получить объём камеры честно.
 * @param {ArrayLike<number>} маска ненулевое значение = воксель внутри камеры
 * @param {number[]} шагМм [x, y, z] в мм
 */
export function объёмПоМаске(маска, шагМм) {
  if (!маска?.length) return { надёжно: false, причина: 'маска пуста', мл: null, вокселей: 0 };
  const [sx, sy, sz] = шагМм || [];
  if (!(sx > 0 && sy > 0 && sz > 0)) {
    return { надёжно: false, причина: 'шаг вокселя неизвестен — объём в миллилитрах не посчитать', мл: null, вокселей: 0 };
  }
  let n = 0;
  for (let i = 0; i < маска.length; i++) if (маска[i]) n++;
  if (!n) return { надёжно: false, причина: 'в маске нет ни одного вокселя', мл: 0, вокселей: 0 };
  return { надёжно: true, причина: null, мл: +(n * sx * sy * sz / 1000).toFixed(1), вокселей: n };
}

/**
 * Объём контраста ВО ВСЁМ КАДРЕ по окну плотности.
 *
 * Возвращается с `этоНеЛЖ: true` намеренно. Такой подсчёт даёт сумму всего
 * контрастированного в кадре — аорта, оба желудочка, предсердия, — и называть
 * это объёмом ЛЖ нельзя. Функция оставлена как грубый ориентир (и для проверки
 * того, что окно плотности вообще что-то находит), но ФВ по ней не считается:
 * для ФВ нужна маска камеры.
 */
export function объёмКонтрастаВКадре(данные, шагМм, окно = { низ: 150, верх: 500 }) {
  if (!данные?.length) return { надёжно: false, причина: 'нет данных объёма', мл: null, этоНеЛЖ: true };
  const [sx, sy, sz] = шагМм || [];
  if (!(sx > 0 && sy > 0 && sz > 0)) {
    return { надёжно: false, причина: 'шаг вокселя неизвестен', мл: null, этоНеЛЖ: true };
  }
  let n = 0;
  for (let i = 0; i < данные.length; i++) {
    const v = данные[i];
    if (v >= окно.низ && v <= окно.верх) n++;
  }
  const мл = +(n * sx * sy * sz / 1000).toFixed(1);
  return {
    надёжно: false,
    причина: 'это объём ВСЕГО контрастированного в кадре (аорта, оба желудочка, предсердия), а не объём ЛЖ — для ФВ нужна маска камеры',
    мл, вокселей: n, этоНеЛЖ: true,
  };
}

export default {
  МЕТОД, фазыИзМетаданных, подписьФазы, выбратьФазыДляФВ, фракцияВыброса,
  объёмПоМаске, объёмКонтрастаВКадре,
};
